import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { className as _$className } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div style="color:#7fdfff;padding:2px 0 4px 0">`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div style=margin-top:4px><div style=display:flex;justify-content:space-between;color:#7fdfff><span></span><span></span></div><input type=range min=-720 max=720 step=15 style=width:100%><button class=toggle-btn style=width:100%;margin-top:2px>RESET TO NOW`),
  _tmpl$3 = /*#__PURE__*/_$template(`<div style=margin-top:5px;color:#d6f2ff><div>SUN AZ &nbsp;<b>&deg; </b></div><div>SUN EL &nbsp;<b>&deg;</b></div><div>SHADOW &nbsp;<b>&deg;</b> (cast toward)</div><div>10m OBJ &nbsp;<b>`),
  _tmpl$4 = /*#__PURE__*/_$template(`<div class=toggle-row style=display:flex;justify-content:space-between;align-items:center;margin-top:8px><span>CAST SHADOWS</span><button>`),
  _tmpl$5 = /*#__PURE__*/_$template(`<div style=color:#6a8fa5;font-size:10px>renders real building shadows &middot; heavy on phone &middot; reload page if the view stops drawing`),
  _tmpl$6 = /*#__PURE__*/_$template(`<div style=color:#ff4d4d;font-size:10px>shadow error: `),
  _tmpl$7 = /*#__PURE__*/_$template(`<div class=right-panel><div class=panel-header>ADVANCE WORK</div><div class=panel-section style=font-family:monospace;font-size:11px;line-height:1.5><div class=toggle-row style=display:flex;justify-content:space-between;align-items:center><span>THREAT HEATMAP</span><button></button></div><div class=toggle-row style=display:flex;justify-content:space-between;align-items:center;margin-top:8px><span>SUN STUDY</span><button></button></div></div><div class=panel-header>PARAMETERS</div><div class="effect-sliders panel-section"></div><div class=panel-header style=margin-top:var(--spacing-lg)>LIVE READOUT</div><div class=live-readout><div class=readout-row><span class=readout-label>LAT</span><span class=readout-value id=readout-lat></span></div><div class=readout-row><span class=readout-label>LNG</span><span class=readout-value id=readout-lng></span></div><div class=readout-row><span class=readout-label>ALT</span><span class=readout-value id=readout-alt></span></div><div class=readout-row><span class=readout-label>GSD</span><span class=readout-value id=readout-gsd></span></div><div class=readout-row><span class=readout-label>NIIRS</span><span class=readout-value id=readout-niirs></span></div><div class=readout-row><span class=readout-label>SUB</span><span class=readout-value id=readout-sub>° EL`),
  _tmpl$8 = /*#__PURE__*/_$template(`<div style=color:#6a8fa5;font-size:10px>`),
  _tmpl$9 = /*#__PURE__*/_$template(`<div style=color:#ff9c4d;margin-top:4px>SUN BELOW HORIZON — no shadow`),
  _tmpl$0 = /*#__PURE__*/_$template(`<div><label></label><input type=range min=0 max=100><span class=slider-value>`);
/**
 * WorldView - Right Panel (SolidJS)
 * Shader/effect controls, live camera readouts, and info panels
 */

import { createSignal, createEffect, onMount, onCleanup, For, Show, Switch, Match, useContext } from "solid-js";
import { shaders, setParameter, PARAMETER_MAPPINGS } from "../stores/shaders";
import { selection } from "../stores/selection";
import { CesiumContext } from "../cesium/CesiumProvider";
import { SatelliteInfo } from "./panels/SatelliteInfo";
import { ShipInfo } from "./panels/ShipInfo";
import { PlaneInfo } from "./panels/PlaneInfo";
import { formatLatitude, formatLongitude, formatAltitude, formatDistance } from "./formatters";
import { groundState, toggleSubLayer, setSunTimeOffset } from "../layers/ground/store";
const SLIDER_NAMES = ["PIXELATION", "DISTORTION", "INSTABILITY"];
export function RightPanel() {
  const cesiumCtx = useContext(CesiumContext);
  const [sliderValues, setSliderValues] = createSignal({
    PIXELATION: 50,
    DISTORTION: 50,
    INSTABILITY: 50
  });
  const [readouts, setReadouts] = createSignal({
    latitude: 0,
    longitude: 0,
    altitude: 0,
    gsd: 0,
    niirs: 0,
    pitch: 0
  });
  const slidersEnabled = () => shaders.active !== null;

  // ---- ADVANCE WORK (roadmap #1 threat heatmap, #2 sun + shadow study) ----

  /** Local wall-clock label for the instant being studied. */
  const studiedTime = () => {
    const d = new Date(Date.now() + groundState.sunTimeOffsetMin * 60_000);
    return d.toLocaleString([], {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  const offsetLabel = () => {
    const m = groundState.sunTimeOffsetMin;
    if (m === 0) return "NOW";
    const sign = m > 0 ? "+" : "-";
    const a = Math.abs(m);
    return `${sign}${Math.floor(a / 60)}h${String(a % 60).padStart(2, "0")}`;
  };
  function getSliderValueFromParams(name, mode) {
    const mapping = PARAMETER_MAPPINGS[mode]?.[name];
    if (!mapping) return 50;
    const paramValue = shaders.parameters[mapping.uniform] ?? mapping.default;
    return Math.round((paramValue - mapping.min) / (mapping.max - mapping.min) * 100);
  }
  function handleSliderChange(name, value) {
    setSliderValues(prev => ({
      ...prev,
      [name]: value
    }));
    const mode = shaders.active;
    if (!mode) return;
    const mapping = PARAMETER_MAPPINGS[mode]?.[name];
    if (mapping) {
      setParameter(mapping.uniform, mapping.min + value / 100 * (mapping.max - mapping.min));
    }
  }
  function updateReadoutsFromCamera() {
    const viewer = cesiumCtx?.viewer();
    if (!viewer) return;
    const {
      camera
    } = viewer;
    const carto = camera.positionCartographic;
    if (!carto) return;
    const altitude = carto.height;
    // GSD: simplified as altitude / 10000 at nadir
    // NIIRS: simplified as 9 - log10(altitude/100), clamped 1-9
    setReadouts({
      latitude: Cesium.Math.toDegrees(carto.latitude),
      longitude: Cesium.Math.toDegrees(carto.longitude),
      altitude,
      gsd: Math.max(0.01, altitude / 10000),
      niirs: Math.max(1, Math.min(9, 9 - Math.log10(altitude / 100))),
      pitch: Cesium.Math.toDegrees(camera.pitch)
    });
  }

  // Sync slider values when shader mode changes
  createEffect(() => {
    const mode = shaders.active;
    if (!mode) {
      setSliderValues({
        PIXELATION: 0,
        DISTORTION: 0,
        INSTABILITY: 0
      });
      return;
    }
    setSliderValues({
      PIXELATION: getSliderValueFromParams("PIXELATION", mode),
      DISTORTION: getSliderValueFromParams("DISTORTION", mode),
      INSTABILITY: getSliderValueFromParams("INSTABILITY", mode)
    });
  });
  onMount(() => {
    const viewer = cesiumCtx?.viewer();
    if (!viewer) return;
    updateReadoutsFromCamera();

    // Throttle camera.changed to max 10 updates/sec
    let lastUpdate = 0;
    const throttledUpdate = () => {
      const now = Date.now();
      if (now - lastUpdate > 100) {
        lastUpdate = now;
        updateReadoutsFromCamera();
      }
    };
    viewer.camera.changed.addEventListener(throttledUpdate);
    viewer.camera.moveEnd.addEventListener(updateReadoutsFromCamera);
    onCleanup(() => {
      viewer.camera.changed.removeEventListener(throttledUpdate);
      viewer.camera.moveEnd.removeEventListener(updateReadoutsFromCamera);
    });
  });
  return [_$createComponent(Switch, {
    get children() {
      return [_$createComponent(Match, {
        get when() {
          return selection.type === "satellite";
        },
        get children() {
          return _$createComponent(SatelliteInfo, {});
        }
      }), _$createComponent(Match, {
        get when() {
          return selection.type === "ship";
        },
        get children() {
          return _$createComponent(ShipInfo, {});
        }
      }), _$createComponent(Match, {
        get when() {
          return selection.type === "flight";
        },
        get children() {
          return _$createComponent(PlaneInfo, {});
        }
      })];
    }
  }), (() => {
    var _el$ = _tmpl$7(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling,
      _el$4 = _el$3.firstChild,
      _el$5 = _el$4.firstChild,
      _el$6 = _el$5.nextSibling,
      _el$8 = _el$4.nextSibling,
      _el$9 = _el$8.firstChild,
      _el$0 = _el$9.nextSibling,
      _el$37 = _el$3.nextSibling,
      _el$38 = _el$37.nextSibling,
      _el$39 = _el$38.nextSibling,
      _el$40 = _el$39.nextSibling,
      _el$41 = _el$40.firstChild,
      _el$42 = _el$41.firstChild,
      _el$43 = _el$42.nextSibling,
      _el$44 = _el$41.nextSibling,
      _el$45 = _el$44.firstChild,
      _el$46 = _el$45.nextSibling,
      _el$47 = _el$44.nextSibling,
      _el$48 = _el$47.firstChild,
      _el$49 = _el$48.nextSibling,
      _el$50 = _el$47.nextSibling,
      _el$51 = _el$50.firstChild,
      _el$52 = _el$51.nextSibling,
      _el$53 = _el$50.nextSibling,
      _el$54 = _el$53.firstChild,
      _el$55 = _el$54.nextSibling,
      _el$56 = _el$53.nextSibling,
      _el$57 = _el$56.firstChild,
      _el$58 = _el$57.nextSibling,
      _el$59 = _el$58.firstChild;
    _el$6.$$click = () => toggleSubLayer("threat");
    _$insert(_el$6, () => groundState.threatHeatmapEnabled ? "ON" : "OFF");
    _$insert(_el$3, _$createComponent(Show, {
      get when() {
        return groundState.threatHeatmapEnabled;
      },
      get children() {
        return [(() => {
          var _el$7 = _tmpl$();
          _$insert(_el$7, () => groundState.threatMeta.status);
          return _el$7;
        })(), _$createComponent(For, {
          get each() {
            return groundState.threatMeta.sources;
          },
          children: src => (() => {
            var _el$60 = _tmpl$8();
            _$insert(_el$60, src);
            return _el$60;
          })()
        })];
      }
    }), _el$8);
    _el$0.$$click = () => toggleSubLayer("sun");
    _$insert(_el$0, () => groundState.sunStudyEnabled ? "ON" : "OFF");
    _$insert(_el$3, _$createComponent(Show, {
      get when() {
        return groundState.sunStudyEnabled;
      },
      get children() {
        return [(() => {
          var _el$1 = _tmpl$2(),
            _el$10 = _el$1.firstChild,
            _el$11 = _el$10.firstChild,
            _el$12 = _el$11.nextSibling,
            _el$13 = _el$10.nextSibling,
            _el$14 = _el$13.nextSibling;
          _$insert(_el$11, studiedTime);
          _$insert(_el$12, offsetLabel);
          _el$13.$$input = e => setSunTimeOffset(parseInt(e.currentTarget.value, 10));
          _el$14.$$click = () => setSunTimeOffset(0);
          _$effect(() => _el$13.value = groundState.sunTimeOffsetMin);
          return _el$1;
        })(), _$createComponent(Show, {
          get when() {
            return groundState.sunMeta.isUp;
          },
          get fallback() {
            return _tmpl$9();
          },
          get children() {
            var _el$15 = _tmpl$3(),
              _el$16 = _el$15.firstChild,
              _el$17 = _el$16.firstChild,
              _el$18 = _el$17.nextSibling,
              _el$19 = _el$18.firstChild,
              _el$20 = _el$16.nextSibling,
              _el$21 = _el$20.firstChild,
              _el$22 = _el$21.nextSibling,
              _el$23 = _el$22.firstChild,
              _el$24 = _el$20.nextSibling,
              _el$25 = _el$24.firstChild,
              _el$26 = _el$25.nextSibling,
              _el$27 = _el$26.firstChild,
              _el$28 = _el$24.nextSibling,
              _el$29 = _el$28.firstChild,
              _el$30 = _el$29.nextSibling;
            _$insert(_el$18, () => groundState.sunMeta.azimuth.toFixed(0), _el$19);
            _$insert(_el$18, () => groundState.sunMeta.compass, null);
            _$insert(_el$22, () => groundState.sunMeta.elevation.toFixed(1), _el$23);
            _$insert(_el$26, () => groundState.sunMeta.shadowBearing.toFixed(0), _el$27);
            _$insert(_el$30, (() => {
              var _c$ = _$memo(() => groundState.sunMeta.shadowLengthM === null);
              return () => _c$() ? "very long" : `${groundState.sunMeta.shadowLengthM.toFixed(0)}m shadow`;
            })());
            return _el$15;
          }
        }), (() => {
          var _el$31 = _tmpl$4(),
            _el$32 = _el$31.firstChild,
            _el$33 = _el$32.nextSibling;
          _el$33.$$click = () => toggleSubLayer("shadows");
          _$insert(_el$33, () => groundState.sunShadowsEnabled ? "ON" : "OFF");
          _$effect(() => _$className(_el$33, `toggle-btn ${groundState.sunShadowsEnabled ? "on" : ""}`));
          return _el$31;
        })(), _tmpl$5(), _$createComponent(Show, {
          get when() {
            return groundState.sunMeta.shadowError;
          },
          get children() {
            var _el$35 = _tmpl$6(),
              _el$36 = _el$35.firstChild;
            _$insert(_el$35, () => groundState.sunMeta.shadowError, null);
            return _el$35;
          }
        })];
      }
    }), null);
    _$insert(_el$38, _$createComponent(For, {
      each: SLIDER_NAMES,
      children: name => (() => {
        var _el$62 = _tmpl$0(),
          _el$63 = _el$62.firstChild,
          _el$64 = _el$63.nextSibling,
          _el$65 = _el$64.nextSibling;
        _$insert(_el$63, name);
        _el$64.$$input = e => handleSliderChange(name, parseInt(e.currentTarget.value, 10));
        _$insert(_el$65, (() => {
          var _c$2 = _$memo(() => !!slidersEnabled());
          return () => _c$2() ? `${sliderValues()[name]}%` : "--";
        })());
        _$effect(_p$ => {
          var _v$3 = `slider-row ${!slidersEnabled() ? "disabled" : ""}`,
            _v$4 = `slider-row-${name.toLowerCase()}`,
            _v$5 = !slidersEnabled(),
            _v$6 = `slider-${name.toLowerCase()}`,
            _v$7 = `slider-value-${name.toLowerCase()}`;
          _v$3 !== _p$.e && _$className(_el$62, _p$.e = _v$3);
          _v$4 !== _p$.t && _$setAttribute(_el$62, "id", _p$.t = _v$4);
          _v$5 !== _p$.a && (_el$64.disabled = _p$.a = _v$5);
          _v$6 !== _p$.o && _$setAttribute(_el$64, "id", _p$.o = _v$6);
          _v$7 !== _p$.i && _$setAttribute(_el$65, "id", _p$.i = _v$7);
          return _p$;
        }, {
          e: undefined,
          t: undefined,
          a: undefined,
          o: undefined,
          i: undefined
        });
        _$effect(() => _el$64.value = sliderValues()[name]);
        return _el$62;
      })()
    }));
    _$insert(_el$43, () => formatLatitude(readouts().latitude));
    _$insert(_el$46, () => formatLongitude(readouts().longitude));
    _$insert(_el$49, () => formatAltitude(readouts().altitude));
    _$insert(_el$52, () => formatDistance(readouts().gsd));
    _$insert(_el$55, () => readouts().niirs.toFixed(1));
    _$insert(_el$58, () => readouts().pitch.toFixed(1), _el$59);
    _$effect(_p$ => {
      var _v$ = `toggle-btn ${groundState.threatHeatmapEnabled ? "on" : ""}`,
        _v$2 = `toggle-btn ${groundState.sunStudyEnabled ? "on" : ""}`;
      _v$ !== _p$.e && _$className(_el$6, _p$.e = _v$);
      _v$2 !== _p$.t && _$className(_el$0, _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$;
  })()];
}
export default RightPanel;
_$delegateEvents(["click", "input"]);